import React, { useState, useEffect, useRef } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import styles from './invoices.module.scss';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { LoadingIcon, CustomLoadingIcon } from '../../supplementary_components/loading_icon';
import { SelectableListView } from '../../general_components/content_management/reusableItems/selectableListView';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { PopupWindow } from '../../main_board_components/teamCard/truckCard/truckCardComponents/popupWindow';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { WebSocketController } from '../../main_board_components/websocketController';
import { useBoardStore } from '../../main_board_components/boardStore';
import { NotFactoredLoadsManager } from './notFactoredLoadsManager';

function sortLoads(loadsArray) {
    return loadsArray.reduce((acc, load) => {
        const { loadStatus, amountForInvoice, invoiceNumber } = load;
        const invoiceAmount = loadStatus === "Invoiced" ||  !invoiceNumber ? amountForInvoice : 0;

        // Use a conditional to find the target category key
        let targetCategory;
        if(amountForInvoice > 0){
            if (loadStatus === "Completed") {
                targetCategory = acc.completed;
            } else if (loadStatus === "ReadyForInvoice" || loadStatus === "Cancelled") {
                targetCategory = acc.readyForInvoice;
            } else if (loadStatus === "Invoiced") {
                targetCategory = acc.invoiced;
            }
        }
        
        
        // If a target category was found, update the nested properties
        if (targetCategory) {
            targetCategory.loads.push(load);
            targetCategory.grandTotal += invoiceAmount;
        }

        return acc;
    }, {
        // --- Initial Accumulator Structure ---
        completed: {
            loads: [],
            grandTotal: 0
        },
        readyForInvoice: {
            loads: [],
            grandTotal: 0
        },
        invoiced: {
            loads: [],
            grandTotal: 0
        }
    });
}
function genetareBlockObj(cat, unfactoredLoads){
    let lbl = "";
    let btn = "";
	let newStatus = "";
    let reverseBtn = null;
    let reverseStatus = null;
    switch (cat) {
        case "completed":
            lbl = "Completed Loads";
            btn = "Mark As Ready For Invoice";
			newStatus = "ReadyForInvoice";
            break;
        case "readyForInvoice":
            lbl = "Ready For Invoice Loads";
            btn = unfactoredLoads ? "Mark As Invoiced" :"Invoice Loads";
            if (unfactoredLoads) {
                reverseBtn = "Mark As Completed";
                reverseStatus = "Completed";
            }
            break;
        case "invoiced":
            lbl = "Invoiced Loads";
            btn = "Mark As Paid"
			newStatus = "Paid";
            break;
        default:
            return "";
    }
    return {
        blockLbl:lbl,
        blockBtn:btn,
		newStatus : newStatus,
        reverseBtn : reverseBtn,
        reverseStatus: reverseStatus
    }
}

function sectionMenuActions(cat, unfactoredLoads) {
    if (unfactoredLoads) return [];
    if (cat === "readyForInvoice") {
        return [
            { label: "Revert to Completed", newStatus: "Completed" },
            { label: "Mark As Invoiced (Without Factoring)", newStatus: "Invoiced" },
        ];
    }
    if (cat === "invoiced") {
        return [
            { label: "Revert to Ready For Invoice", newStatus: "ReadyForInvoice" },
        ];
    }
    return [];
}

export const Invoices = () => {
    // Replaced local state + imperative data bus with Zustand store logic
    const setInvoiceData = useBoardStore(s => s.setInvoiceData);
    const setPopupRaw = useBoardStore(s => s.setPopupRaw);
    const upsertLoad = useBoardStore(s => s.upsertLoad);
    const openPopupLoad = useBoardStore(s => s.openPopupLoad);
    const invoiceIds = useBoardStore(s => s.invoiceIds);
    const loadsById = useBoardStore(s => s.loadsById);
    const notFactoredManagableLoadExists = useBoardStore(s => s.notFactoredManagableLoadExists);
    const completedButNotFactoredLoadsCount = useBoardStore(s => s.completedButNotFactoredLoadsCount);
    const setNotFactoredManagableLoadExists = useBoardStore(s => s.setNotFactoredManagableLoadExists);
    const setCompletedButNotFactoredLoadsCount = useBoardStore(s => s.setCompletedButNotFactoredLoadsCount);
    // Derive the categorized data from the store instead of local state
    // We only re-calculate when loads change. 
    // Since loadsById contains ALL loads (including MainBoard ones), we filter by invoiceIds.
    const data = React.useMemo(() => {
        if(!invoiceIds || invoiceIds.length === 0) return null;
        const currentLoads = invoiceIds.map(id => loadsById[id]).filter(Boolean);
        return sortLoads(currentLoads);
    }, [invoiceIds, loadsById]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
	const [updatingCategory, setUpdatingCategory] = useState(null);

	const updateData = (signal)=>{
        setLoading(true);
		sendRequest('GET', API_ROOT+`loads/invoicesPage`, signal, (respData) => {
			setError(null);
            setInvoiceData(respData?.loads || [], respData ?? null);
            setLoading(false);
			setUpdatingCategory(null);
		}, (e) => {
            setError(e);
            setLoading(false);
        }, null);
	}

    useEffect(
        () => {
            const controller = new AbortController();
            const signal = controller.signal;
            updateData(signal);
            return () => {
                // cancel the request before component unmounts
                controller.abort();
            };
        // eslint-disable-next-line
        }, []
    );

    if (error !== null) return <div className='errorMessage'>{error.customError?error.customError:"Something went wrong"}</div>
    
    // Popup logic: click -> set raw loading state -> fetch detail -> upsert into store -> set raw (or open via ID)
    // Actually, `openPopupLoad(id)` is enough if the load is in store. 
    // But we might want to fetch fresh details like `files` or extended fields if not present.
    // The old code did: `popupWindow.setData({loadNumber:true, loading:true})` then fetch then setData.
    // We can replicate this using `setPopupRaw` for loading state, then `upsertLoad` + `openPopupLoad`.
    
    const customActionOnceClickedOnLoadNumber = (load)=>{
        setPopupRaw({ loadNumber: true, loading: true });
        sendRequest('GET', `${API_ROOT}loads/${load.id}`, null, (resp)=>{
             // Update the store with the full load object
             upsertLoad(resp);
             // Switch popup to view this load ID (which will now pull the full object from store)
             openPopupLoad(resp.id);
        }, ()=>{addNotification({message:`Something went wrong`, type:'alert'})}, null);
    }

    if (loading && !data) return<LoadingIcon state={true} height={'10rem'} />;
    // if (!data) return <div className={styles.noLoadsPresent}>No Loads</div>; // Fallback if data is empty after loading
    
    return <>
        <div className='pageHeader'><div>Invoices</div>
            {notFactoredManagableLoadExists ? <div className={styles.buttonsContainer} >
                <div className={styles.notFactored} onClick={()=>{
                addPopup({
                    content:NotFactoredLoadsManager,
                    popupData: {
                        setNotFactoredManagableLoadExists,
                        setCompletedButNotFactoredLoadsCount,
                    },
                    customWidth: "90%",
                    customMaxWidth: "1700px",
                    customMinWidth: "880px",
                    EZCloseAllowed:true
                })
            }}>Not Factored Loads</div>
                {completedButNotFactoredLoadsCount > 0 ?<span className={styles.notFactoredCount}>{completedButNotFactoredLoadsCount}</span>:null}
            </div> : null}
        </div>
        {
            data ?
            <div className={styles.container}>
                {Object.keys(data).map((category) => {
                    return <CategoryBox key={category} category={category} data={data[category]} customActionOnceClickedOnLoadNumber={customActionOnceClickedOnLoadNumber} updatingCategory={updatingCategory} setUpdatingCategory={setUpdatingCategory} setError={setError} updateData={updateData} />
                })}
    
            </div>
            
            :
            <div className={styles.noLoadsPresent}>No Loads</div>
        }
        
        <PopupWindow />
        <WebSocketController forInvoicePage={true} />
    </>
}

export const CategoryBox = ({category, data, customActionOnceClickedOnLoadNumber, updatingCategory, setUpdatingCategory, setError, updateData, unfactoredLoads})=>{
    const [selectedLoads, setSelectedLoads] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const listRef = useRef(null);
    const menuRef = useRef(null);
    const categoryObject = genetareBlockObj(category, unfactoredLoads);
    const menuActions = sectionMenuActions(category, unfactoredLoads);
	const catLbl = categoryObject.blockLbl;
    const menuDisabled = !selectedLoads.length || !!updatingCategory;

    const resolveSelectedIds = () => {
        if (selectedLoads[0] === "selectAll") {
            return data.loads.map((load) => load.id);
        }
        if (selectedLoads[0] === "ignoreFollowing") {
            return data.loads
                .filter((load) => !selectedLoads.includes(load.id))
                .map((load) => load.id);
        }
        return selectedLoads;
    };

    const clearSelection = () => {
        setSelectedLoads([]);
        const allCheckBoxes = listRef.current.getElementsByClassName("selectableListCheckBox");
        for (let i = 0; i < allCheckBoxes.length; i++) {
            allCheckBoxes[i].checked = false;
        }
    };

    useEffect(() => {
        if (!selectedLoads.length) setMenuOpen(false);
    }, [selectedLoads]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [menuOpen]);

    const changeSelectedStatus = (newStatus) => {
        if (menuDisabled) return;
        setMenuOpen(false);
        setUpdatingCategory(catLbl);
        sendRequest(
            "POST",
            API_ROOT + "loads/changeLoadBatchStatus?newStatus=" + newStatus,
            null,
            () => {
                updateData(null);
                clearSelection();
            },
            (e) => {
                setError(e);
            },
            resolveSelectedIds()
        );
    };

    return <div  ref={listRef} className={`${!updatingCategory || styles.loading}`}>
            <div className={styles.categoryLbl}>
                <div>{catLbl} ({data.loads.length})</div>
                <div>
					{updatingCategory === catLbl ? <CustomLoadingIcon state={true} height={'2rem'} /> : null}
                    {
                        categoryObject.reverseBtn ? <div
                            className={`${styles.reverseActionButton} ${selectedLoads.length || styles.disabledBtn} non-selectable`}
                            onClick={() => {
                                if (!updatingCategory && selectedLoads.length > 0 && categoryObject.reverseStatus) {
                                    setUpdatingCategory(catLbl);
                                    sendRequest(
                                        "POST",
                                        API_ROOT + "loads/changeLoadBatchStatus?newStatus=" + categoryObject.reverseStatus,
                                        null,
                                        () => {
                                            updateData(null);
                                            clearSelection();
                                        },
                                        (e) => {
                                            setError(e);
                                        },
                                        resolveSelectedIds()
                                    );
                                }
                            }}
                        >
                            {categoryObject.reverseBtn}
                        </div> : null
                    }
                    <div  className={`${styles.actionButton} ${selectedLoads.length || styles.disabledBtn} non-selectable`}
						onClick={()=>{
							if(!updatingCategory && selectedLoads.length > 0){
								const selectedIdsArray = resolveSelectedIds();
								setUpdatingCategory(catLbl)
								const link = categoryObject.newStatus ? "loads/changeLoadBatchStatus?newStatus="+categoryObject.newStatus : unfactoredLoads ? "loads/changeLoadBatchStatus?newStatus=Invoiced" :  "loads/invoiceLoads";
								sendRequest("POST", API_ROOT + link, null, (res)=>{
									if(categoryObject.newStatus){
										updateData(null);
										clearSelection();
									}else{
									    addNotification({message:`Invoices Generated`, type:'success'});
                                        updateData(null);
                                        clearSelection();
									}
								}, (e)=>{
									setError(e);
								}, selectedIdsArray, null, null, !categoryObject.newStatus);
								// sendRequest()l
							}
						}}
					>
                        {categoryObject.blockBtn}
                    </div>
					
                    <span>{data.grandTotal > 0 ? priceOnFinish(data.grandTotal, true) :null}</span>
                    {menuActions.length > 0 ? (
                        <div className={styles.sectionMenuAnchor} ref={menuRef}>
                            <button
                                type="button"
                                className={`${styles.sectionMenuButton} ${menuDisabled ? styles.disabledBtn : ""} non-selectable`}
                                aria-label="Section actions"
                                aria-expanded={menuOpen}
                                disabled={menuDisabled}
                                onClick={() => setMenuOpen((open) => !open)}
                            >
                                <BsThreeDotsVertical />
                            </button>
                            {menuOpen ? (
                                <div className={styles.sectionMenu} role="menu">
                                    {menuActions.map((action) => (
                                        <button
                                            key={action.newStatus}
                                            type="button"
                                            role="menuitem"
                                            className={`${styles.sectionMenuItem} non-selectable`}
                                            onClick={() => changeSelectedStatus(action.newStatus)}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        {
            !data.loads || data.loads.length === 0 ? <div className={styles.noLoadsPresent}>No Loads</div> :
            <SelectableListView tblConf={loadListConf} data={data.loads} listRef={listRef} selectedList={selectedLoads} setSelectedList={setSelectedLoads} customAction={customActionOnceClickedOnLoadNumber} />
        }
    </div>
}


const loadListConf = {
    customRowStyling : [{padding:"1.25rem 1rem"}, {margin:"0 0.1rem"}],
    idColumnName: "id",
    columns: [
        {
            lbl: "Load Number",
            name: "loadNumber",
            customActionOnceClicked:true,
            conditionalStyling:()=>{
                return [
                    // {fontWeight:"500"},
                    {cursor:"pointer"},
                    {textDecoration:"underline"},
                    {textDecorationColor:"var(--light-gray)"},
                ]
            }
        },
        {
            lbl: "Broker",
            name: "broker",
            valueExtractor: (load)=>{
                return load.broker.brokerageName;
            },
            customFlexGrow:"2",
            conditionalStyling:(val)=>{
                return [{flexGrow:"2"}];
            },
            conditionalValueStyling:(val)=>{
                if(val === "TEMPORARY BROKER"){
                    const arr = [];
                    arr.push({color:"white"});
                    arr.push({backgroundColor:"var(--alert-color"});
                    // arr.push({textAlign:"center"});
                    arr.push({padding:"0.2rem 0.5rem"});
                    arr.push({borderRadius:"0.4rem"});
                    return arr;
                }
                return [];
            }
        },
        {
            lbl: "Rate",
            name: "amountForInvoice",
            valueExtractor:(load)=>{
                return load.loadStatus !== "Invoiced" && load.invoiceNumber ? "Invoiced" : priceOnFinish(`${load.amountForInvoice}`, true)
            },
            customFlexGrow:"0.5",
            conditionalStyling:(rate)=>{
                const customStyling = [{flexGrow:"0.5"}];
                if(rate === "Invoiced"){
                    customStyling.push({color:"white"});
                    customStyling.push({backgroundColor:"var(--inProgressYellow"});
                    customStyling.push({textAlign:"center"});
                    customStyling.push({padding:"0.2rem 0"});
                    customStyling.push({borderRadius:"0.4rem"});
                }
                return customStyling;
            }
        },
        {
            lbl: "Missing Ppw",
            customFlexGrow:"0.5",
            name: "missingPpw",
            conditionalStyling:(val)=>{
                const result = [{flexGrow:"0.5"}, {fontWeight:"700"}];
                result.push( val === "All Set" ? {color:"var(--main-color)"}: {color:"var(--alert-color)"})
                return   result;
            },
            valueExtractor : (load) =>{
                const fileTypes = getFileTypes(load?.files);
                let result = "All Set";
                if(!fileTypes.includes("Rate Con")){
                    result = "RC";
                }
                if(!fileTypes.includes("POD")){
                    result = result === "All Set" ? "POD" : result+"/POD";
                }
                return result;
            }
        }
    ]
}
// const categoryFor = (status) => {
//     if (status === "Completed") return "completed";
//     if (status === "ReadyForInvoice") return "readyForInvoice";
//     if (status === "Invoiced") return "invoiced";
//     return null; // anything else => not tracked (remove if present)
// };
// const amt = (load) => Number(load?.amountForInvoice ?? 0);
function getFileTypes(filesArray){
    if(!filesArray) return [];
    if(!Array.isArray(filesArray)) return [];
    const result = [];
    for(const entry of filesArray){
        // backend sometimes stores files as JSON strings; sometimes we may already have objects/strings
        if(!entry) continue;
        if(typeof entry === 'string'){
            const trimmed = entry.trim();
            if(trimmed.startsWith('{') || trimmed.startsWith('[')){
                try {
                    const parsed = JSON.parse(trimmed);
                    if(parsed && typeof parsed === 'object' && parsed.fileType) result.push(parsed.fileType);
                    continue;
                } catch (e) {
                    // fallthrough: treat as raw type label
                }
            }
            result.push(trimmed);
            continue;
        }
        if(typeof entry === 'object'){
            if(entry.fileType) result.push(entry.fileType);
        }
    }
    return result;
}
